import type { HeaderSetting, MenuSetting, ProjectConfig } from '#/config';
import type { BeforeMiniState } from '#/store';
import { defineStore } from 'pinia';
import type { DeepPartial } from '@mts-fe/types';
import { deepMerge, rafTimeout } from '@mts-fe/utils';
import { PROJ_CFG_KEY } from '@mts/enums/cacheEnum';
import projectSetting from '@mts/settings/projectSetting';
import { Persistent } from '@mts/utils/cache/persistent';
import { resetRouter } from '@/router';

interface AppState {
  // Page loading status
  pageLoading: boolean;
  appVersion: string;
  // project config
  projectConfig: ProjectConfig | null;
  // When the window shrinks, remember some states, and restore these states when the window is restored
  beforeMiniInfo: BeforeMiniState;
}
export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    pageLoading: false,
    appVersion: '',
    projectConfig: deepMerge(
      projectSetting,
      Persistent.getLocal<ProjectConfig>(PROJ_CFG_KEY) || {},
    ),
    beforeMiniInfo: {},
  }),
  getters: {
    getPageLoading(state): boolean {
      return state.pageLoading;
    },
    getAppVersion(state): string {
      return state.appVersion;
    },

    getBeforeMiniInfo(state): BeforeMiniState {
      return state.beforeMiniInfo;
    },

    getProjectConfig(state): ProjectConfig {
      return state.projectConfig || ({} as ProjectConfig);
    },

    getHeaderSetting(): HeaderSetting {
      return this.getProjectConfig.headerSetting;
    },
    getMenuSetting(): MenuSetting {
      return this.getProjectConfig.menuSetting;
    },
  },
  actions: {
    setPageLoading(loading: boolean): void {
      this.pageLoading = loading;
    },

    setAppVersion(version: string): void {
      this.appVersion = version;
    },

    setBeforeMiniInfo(state: BeforeMiniState): void {
      this.beforeMiniInfo = state;
    },

    setProjectConfig(config: DeepPartial<ProjectConfig>): void {
      this.projectConfig = deepMerge(this.projectConfig || {}, config) as ProjectConfig;
      Persistent.setLocal(PROJ_CFG_KEY, this.projectConfig);
    },
    setMenuSetting(setting: Partial<MenuSetting>): void {
      this.projectConfig!.menuSetting = deepMerge(this.projectConfig!.menuSetting, setting);
      Persistent.setLocal(PROJ_CFG_KEY, this.projectConfig);
    },

    async resetAllState() {
      resetRouter();
      Persistent.clearAll();
    },
    async setPageLoadingAction(loading: boolean): Promise<void> {
      if (loading) {
        clearRaf?.();
        clearRaf = rafTimeout(() => {
          this.setPageLoading(loading);
        }, 50);
      } else {
        this.setPageLoading(loading);
        clearRaf?.();
      }
    },
  },
});
let clearRaf;
